/**
 * Utility functions for handling click-outside events while ignoring
 * specific external modals/popups (e.g., privacy notices, Adobe Target experiments).
 *
 * This prevents our modals/dropdowns from closing when users interact with
 * third-party overlays that are outside our component tree.
 */

/**
 * List of selectors for external modals/popups that should be ignored
 * when determining if a click was "outside" our components.
 */
const IGNORED_EXTERNAL_MODAL_SELECTORS = [
  '#notice-popup', // Privacy notice popup
  '[id^="adobe-exp-"]', // Adobe Target experiment modals (id starts with "adobe-exp-")
];

/**
 * Checks if an element is inside any of the ignored external modals.
 *
 * @param element - The element to check
 * @returns true if the element is inside an ignored external modal
 */
export const isInsideIgnoredExternalModal = (element: Element | null): boolean => {
  if (!element) {
    return false;
  }

  return IGNORED_EXTERNAL_MODAL_SELECTORS.some(selector => {
    // For attribute selectors like [id^="adobe-exp-"], we need to check differently
    if (selector.startsWith('[')) {
      // Use matches to check if any ancestor matches the selector
      let current: Element | null = element;
      while (current) {
        try {
          if (current.matches(selector)) {
            return true;
          }
        } catch {
          // matches() can throw for invalid selectors
        }
        current = current.parentElement;
      }
      return false;
    }

    // For ID selectors, use closest()
    return element.closest?.(selector) !== null;
  });
};

/**
 * Checks if a click event should be ignored for click-outside handling.
 * Returns true if:
 * - The target element is no longer connected to the DOM (was removed)
 * - The target element is inside an ignored external modal
 *
 * @param target - The event target element
 * @returns true if the click should be ignored
 */
export const shouldIgnoreClickOutside = (target: Element | null): boolean => {
  if (!target) {
    return false;
  }

  // If the target is no longer in the DOM (e.g., external modal removed itself),
  // we can't verify where the click was, so ignore it
  if (!target.isConnected) {
    return true;
  }

  // Check if the click was inside an ignored external modal
  return isInsideIgnoredExternalModal(target);
};

/**
 * Checks if a focus event's related target should be ignored for blur/focusout handling.
 * Returns true if the focus moved to an ignored external modal.
 *
 * @param relatedTarget - The event's relatedTarget (where focus moved to)
 * @returns true if the focus change should be ignored
 */
export const shouldIgnoreFocusOutside = (relatedTarget: Element | null): boolean => {
  if (!relatedTarget) {
    return false;
  }

  return isInsideIgnoredExternalModal(relatedTarget);
};
